import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Flex,
  Text,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  useBreakpointValue,
} from '@chakra-ui/react';
import tinycolor from 'tinycolor2';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { useSettings } from '../../stateManagement/SettingsContext';
import { Layout } from '../templates';
import { SearchBar } from '../organisms/SearchBar';
import { CustomText } from '../atoms';
import { RelatedSections } from '../organisms/RelatedSections';
import { Pagination } from '../molecules/Pagination';
import { appInsights } from '../../appInsights';
import { JaggedBorderHeader } from '../atoms/JaggedBorderHeader';
import { CategoriesIcon } from '../atoms/CategoriesIcon';
// import { trackEvent } from '../../Analytics/ga';
import { JaggedBorderHeaderMobile } from '../atoms/JaggedBorderHeaderMobile';

const ITEMS_PER_PAGE = 10; // Number of FAQs per page

export const Section: React.FC = () => {
  const { settings, sectionsData, faqs } = useSettings();
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [sectionBannerUrl, setSectionBannerUrl] = useState<string | null>(null);
  const url = window.location.pathname;

  const sectionTitle = useMemo(() => {
    const match = url.match(/\/section\/([^/]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  }, [url]);

  const language = useMemo(() => {
    const match = url.match(/\/sections\/([^/]+)/);
    return match ? match[1] : '';
  }, [url]);

  const lightenedBorderColor = useMemo(() => {
    return tinycolor(settings?.configs.secondaryTextColor).lighten(90).toString();
  }, [settings?.configs.secondaryTextColor]);

  const sectionToDisplay = useMemo(() => {
    return sectionTitle
      ? sectionsData?.find(
          section => section?.section_name.toLowerCase().replace(/\s+/g, '-') === sectionTitle,
        )
      : sectionsData?.[0] || null;
  }, [sectionTitle, sectionsData]);

  const totalPages = useMemo(() => {
    return Math.ceil((sectionToDisplay?.faq_data.length || 0) / ITEMS_PER_PAGE);
  }, [sectionToDisplay]);

  const currentFaqs = useMemo(() => {
    return sectionToDisplay?.faq_data.slice(
      (currentPage - 1) * ITEMS_PER_PAGE,
      currentPage * ITEMS_PER_PAGE,
    );
  }, [sectionToDisplay, currentPage]);

  const onContactUsClick = (): void => {
    // trackEvent('contact_us_button_clicked', {
    //   source: 'section_page',
    //   section_id: sectionToDisplay?.section_id,
    //   section_name: sectionToDisplay?.section_name,
    // });
    navigate(`/contactUs`);
  };

  const handleArticleClick = (publish_id: string) => {
    const faq = faqs?.find(item => item.publish_id === publish_id);
    const translation = faq?.translations[language];
    if (translation) {
      const formattedTitle = translation.title.toLowerCase().replace(/\s+/g, '-');
      navigate(`/article/${language}/faq/${publish_id}-${formattedTitle}`);
      appInsights.trackEvent({
        name: 'landingPage',
        properties: {
          buttonName: 'allFaqTile',
          userName: 'anonymous',
          publishId: publish_id,
          articleTitle: formattedTitle,
          category: sectionToDisplay?.section_name,
          articleId: faq.id,
          sectionId: faq.section_id,
          language,
          // articleId
        },
      });
    }
  };

  const articleHref = (publish_id: string) => {
    const faq = faqs?.find(item => item.publish_id === publish_id);
    const translation = faq?.translations[language];
    if (translation) {
      const formattedTitle = translation.title.toLowerCase().replace(/\s+/g, '-');
      return `/article/${language}/faq/${publish_id}-${formattedTitle}`;
    }
    return '#';
  };

  const { t }: { t: (key: string, options?: { returnObjects: boolean }) => any } = useTranslation();
  const helperTexts = t('data.helperTexts', { returnObjects: true });
  const categoryHeadings = t('data.categoryHeadings', { returnObjects: true });

  useEffect(() => {
    if (sectionToDisplay) {
      window.scrollTo(0, 0);
    }
  }, [sectionToDisplay]);

  if (!sectionToDisplay) {
    return (
      <Box
        px={{ base: '2vw', md: '1vw', lg: '15vw' }}
        backgroundColor={settings?.configs.primaryBgColor}
        textAlign="center"
        py={8}
      >
        <Text fontSize="2xl" color={settings?.configs.secondaryTextColor}>
          404 - Article Not Found
        </Text>
        <Text fontSize="lg" color={settings?.configs.secondaryTextColor}>
          The requested article could not be found. Please check the URL or try again later.
        </Text>
      </Box>
    );
  }

  useEffect(() => {
    if (sectionToDisplay?.section_id) {
      const cdnurl = process.env.REACT_APP_CDN_URL;
      const imageUrl = `${cdnurl}/section-images/${sectionToDisplay.section_id}.png`;

      // Optionally check if image exists
      fetch(imageUrl, { method: 'HEAD' })
        .then(res => {
          if (res.ok) {
            setSectionBannerUrl(imageUrl);
          } else {
            setSectionBannerUrl(null); // fallback or don't set
          }
        })
        .catch(() => {
          setSectionBannerUrl(null);
        });
    }
  });

  const isMobile = useBreakpointValue({ base: true, md: false });

  return (
    <Layout
      pageBgColor={settings?.configs.primaryBgColor}
      onContactUsClick={onContactUsClick}
      settings={settings}
    >
      <Helmet>
        <title> {sectionToDisplay.section_name}</title>
      </Helmet>
      <Box
        width="100vw"
        height={{ base: '35vh', md: '45vh', lg: '50vh' }}
        backgroundImage={`url(${sectionBannerUrl || settings?.configs.bannerImageUrl})`}
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundRepeat="no-repeat"
        display="flex"
        alignItems="center"
        justifyContent="center"
        px={{ base: '4vw', md: '10vw', lg: '20vw' }}
        position="relative"
      >
        <Box width="100%" display="flex" justifyContent="center" zIndex={2}>
          <Box width="100%" mt="150px">
            <SearchBar
              iconColor={settings?.configs.secondaryAccentColor}
              searchBarDropDownTextColor={settings?.configs.secondaryAccentColor}
              placeholderText="Search for articles"
              searchBarPx={{ base: '5vw', md: '3vw', lg: '1vw' }}
              searchBarTextColor={settings?.configs.searchBarTextColor}
              placeholderFontSize={{ base: '20px', md: '22px', lg: '24px' }} // Responsive font size
              placeholderFontWeight="bold"
            />
          </Box>
        </Box>
        {settings?.configs.showJaggedBorder &&
          (isMobile ? (
            <JaggedBorderHeaderMobile bgColor={settings.configs.primaryBgColor} />
          ) : (
            <JaggedBorderHeader bgColor={settings.configs.primaryBgColor} />
          ))}{' '}
      </Box>

      <Box
        px={{ base: '2vw', md: '1vw', lg: '14vw' }}
        backgroundColor={settings?.configs.primaryBgColor}
        py={{ base: '2vh', lg: '2vh' }}
        overflow="visible"
      >
        <Flex mt={8} gap={8} flexDirection={{ base: 'column', lg: 'row' }}>
          <Box flex="2" backgroundColor="#fff" p={4}>
            <Breadcrumb
              separator="/"
              fontSize="xs"
              color={settings?.configs.secondaryAccentColor}
              p={4}
            >
              <BreadcrumbItem>
                <a
                  href="/"
                  onClick={e => {
                    const isLeftClick = e.button === 0;
                    const isModified = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;

                    if (isLeftClick && !isModified) {
                      e.preventDefault();
                      navigate('/');
                    }
                  }}
                >
                  <BreadcrumbLink color={settings?.configs.secondaryAccentColor}>
                    Home
                  </BreadcrumbLink>
                </a>
              </BreadcrumbItem>
              <BreadcrumbItem isCurrentPage>
                <BreadcrumbLink color={settings?.configs.secondaryAccentColor}>
                  {sectionToDisplay.section_name}
                </BreadcrumbLink>
              </BreadcrumbItem>
            </Breadcrumb>

            <Box my={6} p={4}>
              <CustomText variant="h1" fontSize="4xl">
                {sectionToDisplay.section_name}
              </CustomText>
            </Box>

            {currentFaqs?.map(faq => (
              <a href={articleHref(faq.publish_id)}>
                <Box
                  key={faq.publish_id}
                  borderBottom={`1px solid ${lightenedBorderColor}`}
                  p={4}
                  _hover={{ boxShadow: 'md', transform: 'scale(1.01)' }}
                  onClick={e => {
                    const isLeftClick = e.button === 0;
                    const isModifiedClick = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
                    // SPA routing on normal click
                    if (isLeftClick && !isModifiedClick) {
                      e.preventDefault();
                      handleArticleClick(faq.publish_id);
                    }
                  }}
                >
                  <Text
                    fontSize="md"
                    fontWeight="bold"
                    color={settings?.configs.secondaryTextColor}
                    noOfLines={1}
                  >
                    {faq.faq_title}
                  </Text>
                  <Box
                    dangerouslySetInnerHTML={{ __html: faq.faq_body }}
                    color={settings?.configs.secondaryTextColor}
                    fontSize="sm"
                    style={{
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: 2,
                      overflow: 'hidden',
                    }}
                  />
                </Box>
              </a>
            ))}
            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                nextText={helperTexts.find((item: Record<string, string>) => 'next' in item)?.next}
                previousText={
                  helperTexts.find((item: Record<string, string>) => 'previous' in item)?.previous
                }
                pageText={helperTexts.find((item: Record<string, string>) => 'page' in item)?.page}
                ofText={helperTexts.find((item: Record<string, string>) => 'of' in item)?.of}
              />
            )}
          </Box>

          <Box
            flex="1"
            backgroundColor={settings?.configs.primaryBgColor}
            onClick={() => setCurrentPage(1)}
          >
            <Box mb={2}>
              <CustomText fontWeight="bold" variant="h1">
                <Box display="inline-flex" alignItems="center" gap="2">
                  <CategoriesIcon color={settings?.configs.primaryAccentColor} />
                  {
                    categoryHeadings.find(
                      (item: Record<string, string>) => 'sectionsHeading' in item,
                    )?.sectionsHeading
                  }
                </Box>
              </CustomText>
              <Box height="3px" width="60px" background={settings?.configs.gradientAccentColor} />
              <Box
                height="1px"
                width="100%"
                background={settings?.configs.gradientAccentColor}
                mb="6"
              />
            </Box>
            <RelatedSections
              sectionsData={sectionsData}
              bgColor={settings?.configs.secondaryBgColor}
              textColor={settings?.configs.secondaryTextColor}
            />
          </Box>
        </Flex>
      </Box>
    </Layout>
  );
};
